/**
 * Phone Number Validation Utilities
 * Supports international phone numbers with common formats.
 *
 * Valid formats:
 *   +1 202-555-0143  |  +44 7700 900123  |  +91 9876543210
 *   (202) 555-0143   |  2025550143       |  +81-90-1234-5678
 *
 * Rules:
 *   - Optional leading "+" for country code (1–3 digits)
 *   - Allowed separators: spaces, dashes (-), dots (.), parentheses ()
 *   - Total digit count (separators excluded) must be 8–15
 *   - Letters and unsupported symbols are rejected
 */

/**
 * Extracts only digits from a phone number string.
 * @param {string} phoneNumber
 * @returns {string}
 */
export const extractDigits = (phoneNumber) => {
  return phoneNumber.replace(/[^0-9]/g, '');
};

/**
 * Validates an international phone number.
 * @param {string} phoneNumber
 * @returns {{ isValid: boolean, error: string|null }}
 */
export const validatePhoneNumber = (phoneNumber) => {
  if (!phoneNumber || !phoneNumber.trim()) {
    return { isValid: false, error: 'Please enter a phone number.' };
  }

  const trimmed = phoneNumber.trim();

  // Reject any character that is not: digit, +, space, dash, dot, parenthesis
  if (/[^0-9+\s\-.()\t]/.test(trimmed)) {
    return {
      isValid: false,
      error: 'Invalid phone number. Only digits, +, spaces, dashes, dots, and parentheses are allowed.',
    };
  }

  // "+" is only allowed once and only at the very start
  const plusCount = (trimmed.match(/\+/g) || []).length;
  if (plusCount > 1 || (plusCount === 1 && trimmed[0] !== '+')) {
    return {
      isValid: false,
      error: 'Invalid phone number. The "+" sign must only appear at the start.',
    };
  }

  const digits = extractDigits(trimmed);

  if (digits.length < 8) {
    return {
      isValid: false,
      error: `Phone number is too short. At least 8 digits are required (currently ${digits.length}).`,
    };
  }

  if (digits.length > 15) {
    return {
      isValid: false,
      error: `Phone number is too long. Maximum 15 digits allowed (currently ${digits.length}).`,
    };
  }

  return { isValid: true, error: null };
};

/**
 * Sanitizes input for a phone number field.
 * Keeps digits, a leading "+", spaces, dashes, dots, and parentheses.
 * Limits total length to 20 characters (15 digits + formatting).
 * @param {string} phoneNumber
 * @returns {string}
 */
export const sanitizePhoneNumber = (phoneNumber) => {
  // Remove every character that is not allowed
  let sanitized = phoneNumber.replace(/[^0-9+\s\-.()\t]/g, '');

  // Keep "+" only if it is the very first character
  if (sanitized.indexOf('+') > 0) {
    sanitized = sanitized.replace(/\+/g, '');
  } else if (sanitized.indexOf('+') === 0) {
    // Preserve leading +, strip any other + occurrences
    sanitized = '+' + sanitized.slice(1).replace(/\+/g, '');
  }

  return sanitized.slice(0, 20);
};

/**
 * Formats a 10-digit local number for display: (XXX) XXX-XXXX.
 * Returns the original string for any other length.
 * @param {string} phoneNumber
 * @returns {string}
 */
export const formatPhoneNumber = (phoneNumber) => {
  const digits = extractDigits(phoneNumber);
  if (digits.length !== 10) return phoneNumber;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export default {
  validatePhoneNumber,
  sanitizePhoneNumber,
  extractDigits,
  formatPhoneNumber,
};
